import Address from '@atoms/Address/Address';
import Fieldset from '@atoms/Fieldset/Fieldset';
import CheckboxInput from '@atoms/Inputs/CheckboxInput';
import RichText from '@atoms/RichText/RichText';
import Time from '@atoms/Time/Time';
import VacancyDetails from '@molecules/VacancyDetails/VacancyDetails';
import ChevronDownIcon from '@svg/chevron-up.svg';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { forwardRef, useState } from 'react';

import styles from './ApplicationVacancies.module.scss';

const vacancyLocationType = PropTypes.shape({
  id: PropTypes.number.isRequired,
  vacancyCode: PropTypes.string.isRequired,
  locationTitle: PropTypes.string,
  locationCode: PropTypes.string.isRequired,
  minHours: PropTypes.number,
  maxHours: PropTypes.number,
  minSalary: PropTypes.string,
  maxSalary: PropTypes.string,
  closingDate: PropTypes.string,
  vacanciesCount: PropTypes.number,
  hasDetails: PropTypes.bool,
  usps: PropTypes.string,
  address: PropTypes.string,
  postal: PropTypes.string,
  city: PropTypes.string,
  phone: PropTypes.string,
});

function ApplicationVacancyItem({
  vacancy,
  handleChange,
  vacancyApplicationError,
}) {
  const [showLocationDetails, setShowLocationDetails] = useState(false);
  return (
    <CheckboxInput
      id={vacancy.id}
      name="selectedVacancies"
      label={`${vacancy.locationTitle}${
        vacancy.vacanciesCount > 1
          ? ` - ${vacancy.vacanciesCount} vacatures`
          : ''
      }`}
      value={vacancy.vacancyCode}
      onChange={handleChange}
      modifier="applicationLocation"
      groupCheckbox
      error={vacancyApplicationError}
    >
      <>
        <VacancyDetails
          details={{
            minHours: vacancy.minHours,
            maxHours: vacancy.maxHours,
            minSalary: vacancy.minSalary,
            maxSalary: vacancy.maxSalary,
          }}
          isCompact
          hideEducation
          hideLocation
        />
        {vacancy.hasDetails && (
          <>
            <button
              type="button"
              className={styles.toggle}
              onClick={() => setShowLocationDetails(!showLocationDetails)}
              aria-haspopup
              aria-expanded={showLocationDetails}
              aria-controls="location-details"
              aria-label={
                showLocationDetails
                  ? 'Locatie details verbergen'
                  : 'Locatie details tonen'
              }
            >
              <ChevronDownIcon
                className={classNames(
                  styles.toggleIcon,
                  showLocationDetails && styles.isActive
                )}
              />
            </button>
            <div
              id="location-details"
              className={classNames(
                styles.locationDetails,
                showLocationDetails && styles.showLocationDetails
              )}
            >
              {vacancy.usps && <RichText text={vacancy.usps} />}
              <Address
                withIcon
                modifier="applicationLocation"
                address={vacancy.address}
                city={vacancy.city}
                postal={vacancy.postal}
                phone={vacancy.phone}
              />
            </div>
          </>
        )}
        <p className={styles.closingDate}>
          Sluitingsdatum: <Time date={vacancy.closingDate} />
        </p>
      </>
    </CheckboxInput>
  );
}

ApplicationVacancyItem.propTypes = {
  vacancy: vacancyLocationType,
  handleChange: PropTypes.func,
  vacancyApplicationError: PropTypes.bool,
};

const ApplicationVacancies = forwardRef(
  ({ loading, vacancies, handleChange, vacancyApplicationError }, ref) => (
    <Fieldset
      legend="Op welke vacature(s) wil je solliciteren?"
      disabled={loading}
    >
      <ul className="u-list-clean u-mb-0" ref={ref}>
        {vacancies.map(vacancy => (
          <li key={vacancy.id} className={styles.applicationLocation}>
            <ApplicationVacancyItem
              vacancy={vacancy}
              handleChange={handleChange}
              vacancyApplicationError={vacancyApplicationError}
            />
          </li>
        ))}
      </ul>
      <span
        className={styles.vacancyApplicationError}
        hidden={!vacancyApplicationError}
      >
        Kies tenminste één vacature om op te solliciteren
      </span>
    </Fieldset>
  )
);

ApplicationVacancies.displayName = 'ApplicationVacancies';

ApplicationVacancies.propTypes = {
  loading: PropTypes.bool,
  vacancies: PropTypes.arrayOf(vacancyLocationType),
  handleChange: PropTypes.func,
  vacancyApplicationError: PropTypes.bool,
};

export default ApplicationVacancies;
